import { FormEvent, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useStyle } from "./style";
import { IPortin } from "../../ICart";
import { format, parse } from "date-fns";
import { AccountNumber } from "./AccountNumber/AccountNumber";
import { HandleErrorAndLoading } from "@common/HandleErrorAndLoading/HandleErrorAndLoading";
import { LabeledTextField } from "@common/LabeledTextField/LabeledTextField";
import { ValidatePhoneNumber } from "@features/portIn/validatePhoneNumber/ValidatePhoneNumber";
import { IPortinForm } from "@interface/IPortinForm";
import { PaymentType } from "@features/portIn/paymentType/PaymentType";
import { PortInDate } from "@features/portIn/date/PortInDate";
import { PortInTimeRange } from "@features/portIn/timeRange/PortInTimeRange";
import { cls, IFetchState } from "itsf-ui-common";
import { FormGroup, Grid, Stack, Typography } from "@mui/material";
import { LabeledButton } from "@common/LabeledButton/LabeledButton";
import { usePortIn } from "./usePortIn";
import { useDeviceType } from "@hooks/useDeviceType";
import { PAYMENT_TYPE_BILL } from "@constants";
import { getObjectByPath } from "@utils/getObjectByPath";
import type { FieldError } from "react-hook-form";
import type { IPortinSlot } from "@features/portIn/IPortinSlot";

interface ICartPortInFormValues {
    portIn: Record<string, IPortinForm>;
}

interface IParams {
    offerId: number;
    addPortin(cartOfferId: number, portinForm: IPortin, closePortInForm: () => void): void;
    closePortInForm(): void;
    getPortinState: IFetchState;
    getPortinSlotState: IFetchState;
    getPortinSlot(): void;
    portinSlotList: IPortinSlot[];
    portinInfo?: IPortin;
    isPortInCardError?: boolean;
    setIsPortInCardError?(isPortInCardError: boolean): void;
}

export const PortIn = (props: IParams) => {
    const {
        closePortInForm,
        getPortinState,
        portinInfo,
        addPortin,
        offerId,
        getPortinSlot,
        getPortinSlotState,
        portinSlotList,
        setIsPortInCardError,
    } = props;
    const { isMobile } = useDeviceType();
    const [isFinish, setIsFinish] = useState(Boolean(portinInfo));
    const [phoneNumberSelected, setPhoneNumberSelected] = useState(portinInfo?.phoneNumber ?? "");
    const [currentProvider, setCurrentProvider] = useState(
        portinInfo?.otherNetworkId
            ? { id: portinInfo?.otherNetworkId, networkName: portinInfo?.otherNetworkName }
            : undefined
    );
    const formMethods = useFormContext<ICartPortInFormValues>();
    const { clearErrors, errors, getValues, register, trigger } = formMethods;
    const namePrefix = `portIn.${offerId}.`;
    const clearCardError = () => setIsPortInCardError?.(false);

    const paymentTypeError = getObjectByPath<FieldError>(errors, `${namePrefix}paymentType`);
    const accountNumberError = getObjectByPath<FieldError>(errors, `${namePrefix}accountNumber`);

    const { isPostPay } = usePortIn({
        formMethods,
        namePrefix,
    });

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!isFinish) {
            return;
        }

        const fieldsToValidate = [`${namePrefix}paymentType`, `${namePrefix}date`, `${namePrefix}time`];
        if (isPostPay) {
            fieldsToValidate.push(`${namePrefix}accountNumber`);
        }

        const formValues = getValues();

        const isValid = await trigger(fieldsToValidate);
        if (!isValid) {
            setIsPortInCardError?.(true);

            return;
        }

        clearCardError();
        const scopedFormData: IPortinForm = getObjectByPath<IPortinForm>(formValues, `portIn.${offerId}`) ?? {
            accountNumber: "",
            date: null,
            paymentType: "",
            phoneNumber: "",
            time: "",
            validationCode: "",
        };
        const accountNumberInput = scopedFormData.paymentType.includes(PAYMENT_TYPE_BILL)
            ? scopedFormData.accountNumber
            : "";

        const portStartDateTime = parse(scopedFormData.time, "HH:mm", scopedFormData.date ?? new Date()).toISOString();

        const portin = {
            otherNetworkAccountNumber: accountNumberInput,
            otherNetworkId: currentProvider ? currentProvider.id : undefined,
            otherNetworkName: currentProvider ? currentProvider.networkName : "",
            phoneNumber: phoneNumberSelected,
            portStartDateTime,
            type: scopedFormData.paymentType,
        };

        addPortin(offerId, portin, closePortInForm);
    };

    const previousPhoneNumber = portinInfo?.phoneNumber ?? "";
    const { t } = useTranslation(["cart", "common", "portin"]);
    const { classes } = useStyle();
    const paymentTypeFieldName = `${namePrefix}paymentType`;
    const accountNumberFieldName = `${namePrefix}accountNumber`;
    const defaultPortStartDate = portinInfo?.portStartDateTime ? new Date(portinInfo.portStartDateTime) : null;
    const defaultTime = defaultPortStartDate ? format(defaultPortStartDate, "HH:mm") : "";

    return (
        <div
            className={classes.container}
            key={`${offerId}-${Boolean(portinInfo)}-${portinInfo?.portStartDateTime ?? ""}`}
        >
            <HandleErrorAndLoading
                error={getPortinSlotState.error}
                isLoading={getPortinSlotState.isLoading || getPortinState.isLoading}
                retryFetch={getPortinSlot}
            >
                <form className={`xtest_portin_form ${classes.form}`} onSubmit={submit}>
                    <Grid container direction="column" spacing={2}>
                        <Grid item>
                            <Typography className={classes.portInInfo} color="secondary" variant="normal">
                                {t("cart:portInInfo")}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <section className="form-section">
                                <FormGroup>
                                    <Grid container spacing={2}>
                                        {!isFinish && (
                                            <Grid item xs={12}>
                                                <ValidatePhoneNumber
                                                    clearCardError={clearCardError}
                                                    formMethods={formMethods}
                                                    namePrefix={namePrefix}
                                                    phoneNumberSelected={phoneNumberSelected}
                                                    previousPhoneNumber={previousPhoneNumber}
                                                    setCurrentProvider={setCurrentProvider}
                                                    setIsFinished={() => setIsFinish(true)}
                                                    setPhoneNumberSelected={setPhoneNumberSelected}
                                                />
                                            </Grid>
                                        )}
                                        {isFinish && (
                                            <>
                                                <Grid item md={4} sm={12}>
                                                    <LabeledTextField
                                                        fullWidth
                                                        helperText={t("portin:mobileNumberValidated")}
                                                        id="portin-phoneNumber"
                                                        isReadOnly
                                                        isRequired
                                                        labelText={t("portin:phoneNumber")}
                                                        value={phoneNumberSelected}
                                                    />
                                                </Grid>
                                                <Grid item md={4} sm={12}>
                                                    <LabeledTextField
                                                        fullWidth
                                                        id="portin-currentProvider"
                                                        isReadOnly
                                                        labelText={t("portin:currentProvider")}
                                                        value={currentProvider?.networkName ?? ""}
                                                    />
                                                </Grid>
                                                <Grid item md={4} sm={12}>
                                                    <div className={classes.accountTypeColumn}>
                                                        <PaymentType
                                                            clearCardError={clearCardError}
                                                            control={formMethods.control}
                                                            defaultValue={portinInfo?.type ?? ""}
                                                            error={paymentTypeError}
                                                            namePrefix={namePrefix}
                                                            onClearError={() => {
                                                                clearErrors(paymentTypeFieldName);
                                                            }}
                                                        />
                                                        <Typography color="secondary" variant="small">
                                                            {t("portin:accountTypeDescription")}
                                                        </Typography>
                                                    </div>
                                                </Grid>
                                            </>
                                        )}
                                    </Grid>
                                </FormGroup>
                                {isFinish && (
                                    <FormGroup>
                                        <Grid className={classes.dateTimeRow} container spacing={2}>
                                            {isPostPay && (
                                                <Grid item xs={12}>
                                                    <AccountNumber
                                                        defaultValue={portinInfo?.otherNetworkAccountNumber ?? ""}
                                                        error={accountNumberError}
                                                        namePrefix={namePrefix}
                                                        register={register}
                                                        onChange={() => {
                                                            clearErrors(accountNumberFieldName);
                                                            clearCardError();
                                                        }}
                                                    />
                                                </Grid>
                                            )}
                                            <Grid item sm={6} xs={12}>
                                                <PortInDate
                                                    clearCardError={clearCardError}
                                                    defaultValue={defaultPortStartDate}
                                                    formMethods={formMethods}
                                                    fullWidth
                                                    namePrefix={namePrefix}
                                                    portinSlotList={portinSlotList}
                                                />
                                            </Grid>
                                            <Grid item sm={6} xs={12}>
                                                <div className={classes.preferredTime}>
                                                    <PortInTimeRange
                                                        clearCardError={clearCardError}
                                                        defaultValue={defaultTime}
                                                        formMethods={formMethods}
                                                        namePrefix={namePrefix}
                                                        portinSlotList={portinSlotList}
                                                    />
                                                    <Typography color="secondary" variant="small">
                                                        {t("portin:moveSlotHelper")}
                                                    </Typography>
                                                </div>
                                            </Grid>
                                        </Grid>
                                    </FormGroup>
                                )}
                            </section>
                            {isFinish && (
                                <section className={classes.submitSection}>
                                    <Stack
                                        alignItems="center"
                                        flexDirection={isMobile ? "column-reverse" : "row"}
                                        gap={2}
                                        justifyContent={isMobile ? "center" : "flex-end"}
                                    >
                                        <LabeledButton
                                            className={cls("xtest_cancel_button", classes.cancelButton)}
                                            color="primary"
                                            id="cancelButton"
                                            labelText={t("common:cancel")}
                                            size="large"
                                            variant="text"
                                            onClick={closePortInForm}
                                        >
                                            {t("common:cancel")}
                                        </LabeledButton>
                                        <LabeledButton
                                            className="xtest_submit_button"
                                            color="primary"
                                            id="submitButton"
                                            labelText={t("portin:confirmDetails")}
                                            size="large"
                                            type="submit"
                                            variant="contained"
                                        >
                                            {t("portin:confirmDetails")}
                                        </LabeledButton>
                                    </Stack>
                                </section>
                            )}
                        </Grid>
                    </Grid>
                </form>
            </HandleErrorAndLoading>
        </div>
    );
};

PortIn.displayName = "PortIn";
